import { ListItemIcon, Typography } from '@mui/material';
import { FieldArray, FieldArrayRenderProps, FormikProps } from 'formik';
import { useRef } from 'react';
import { useDrop } from 'react-dnd';
import { useIntl } from 'react-intl';
import { DragAndDropTypes } from '../../../../model/dragAndDropTypes';
import { PolicyRule } from '../../../../model/interfaces/policyRule';
import { ScoreModel } from '../../../../model/interfaces/scoreModel';
import { ScrollableStack } from '../../../../modules/components/scrollableStack';
import RemovableListing from '../../../../modules/listings/removableListing';
import { DragIndicator } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

interface ScoreModelEditorActiveRulesProps {
  values: ScoreModel;
  formRef: React.MutableRefObject<FormikProps<ScoreModel> | null>;
}

export const ScoreModelEditorActiveRules = (props: ScoreModelEditorActiveRulesProps) => {
  const { values, formRef } = props;
  const arrayHelpersRef = useRef<FieldArrayRenderProps | null>(null);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const handleDrop = (newRule: PolicyRule) => {
    const idExistsInArray = formRef.current?.values.policyRules.some(
      (policyRule) => policyRule.policyRuleId === newRule.policyRuleId
    );
    if (!idExistsInArray) {
      arrayHelpersRef.current?.push(newRule);
    } else {
      enqueueSnackbar(`'${newRule.name}' already exists in this ScoreModel`, {
        variant: 'info'
      });
    }
  };

  const [, drop] = useDrop(() => ({
    accept: DragAndDropTypes.RULELISTING,
    drop: (item: { rule: PolicyRule }) => handleDrop(item.rule),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop()
    })
  }));

  return (
    <>
      <Typography variant={'body1'} paddingLeft={1} fontWeight="bold">
        {intl.formatMessage({
          id: 'ActiveRules'
        })}
      </Typography>
      <ScrollableStack spacing={1} ref={drop}>
        <FieldArray name={'policyRules'}>
          {(arrayHelpers) => {
            arrayHelpersRef.current = arrayHelpers;
            return values.policyRules.map((policyRule, idx) => (
              <RemovableListing
                rule={policyRule}
                onClick={() => {
                  arrayHelpersRef.current?.remove(idx);
                }}
                key={policyRule.policyRuleId}
                {...policyRule}
              />
            ));
          }}
        </FieldArray>
        <ListItemIcon>
          <DragIndicator />
          <Typography variant={'body2'}>Drag rule to add new to the score model...</Typography>
        </ListItemIcon>
      </ScrollableStack>
    </>
  );
};
